import type { TableConfigurationJson } from '@repo/schema/table-configuration'

/**
 * A table registered for Table Configuration persistence. The definition owns the table's
 * query semantics; columns are validated against the mounted table's code defaults.
 */
export type TableConfigurationDefinition<TQuery extends TableConfigurationJson> = {
  /** Code-default query applied when a view falls back to Default. */
  defaultQuery: TQuery
  /** The table's name in copy, such as `Loads`. */
  label: string
  pathname: string
  /** Permission the current User needs before any Table Configuration request is made. */
  permission: string
  query: {
    /**
     * Validate a stored query, migrating supported older schema versions in memory. Invalid
     * fields fall back to code defaults; an unsupported future version returns `null`.
     */
    decode: (_stored: TableConfigurationJson, _schemaVersion: number) => null | TQuery
    /** Turn the live query into the one a save stores. Omit to store the live query. */
    encode?: (_query: TQuery) => TQuery
    equals: (_a: TQuery, _b: TQuery) => boolean
    /** Read the normalized live query from the table URL. */
    read: (_search: URLSearchParams) => TQuery
    /** Write a query into the table URL, dropping default values and the current page. */
    write: (_search: URLSearchParams, _query: TQuery) => URLSearchParams
  }
  schemaVersion: number
  tableKey: string
}

/** A Saved View's own path under its table; filters changed on top of it go in the search. */
export const viewPath = (
  definition: Pick<TableConfigurationDefinition<TableConfigurationJson>, 'pathname'>,
  id: number | string,
) => `${definition.pathname}/view/${encodeURIComponent(id)}`
